/**
 * Custom hook for managing Discovery step chat logic.
 * Extracts business logic from DiscoveryStep component for better separation of concerns.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Message, MessageAttachment } from '../../common/ChatInterface';
import {
  useWorkflow,
  type ChatHistoryMessage,
} from '../../../contexts/WorkflowContext';
import {
  workflowService,
  type ChatResponse,
  type QualityReport,
  type StepStructuredData,
} from '../../../services/workflow';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { FileAttachment } from '../../../types/upload';
import type {
  ProblemStatementDraft,
  SectionFeedback,
} from './ProblemStatementEditor';
import Logger from '../../../utils/logger';

const logger = Logger.create('useDiscoveryChat');

// Default welcome message
const WELCOME_MESSAGE: Message = {
  id: '1',
  role: 'assistant',
  content:
    "Hello! I'm your Discovery Coach. Let's explore what problem you want to solve with an AI agent. Tell me about your idea, and I'll help you refine it into a clear problem statement. After a couple of exchanges, I'll create a draft for us to iterate on together.\n\nYou can also attach documents or images (requirements docs, mockups, diagrams) to help me understand your needs better.",
  timestamp: Date.now(),
};

export interface UseDiscoveryChatOptions {
  initialChatHistory?: ChatHistoryMessage[];
  initialSpecData?: StepStructuredData | null;
  initialQualityScore?: number;
  initialQualityReport?: QualityReport;
}

export interface UseDiscoveryChatResult {
  // Messages
  messages: Message[];

  // Loading/error states
  isLoading: boolean;
  error: string | null;
  clearError: () => void;

  // Draft and quality
  draft: ProblemStatementDraft | null;
  qualityScore: number;
  qualityScoreLabel: string;
  sectionFeedback: SectionFeedback[];
  qualityReport: QualityReport | null;
  clarityPercent: number;

  // Save state
  isSaving: boolean;

  // Actions
  sendMessage: (
    content: string,
    fileAttachments?: FileAttachment[]
  ) => Promise<void>;
  saveDraft: (newDraft: ProblemStatementDraft) => Promise<void>;
  askAboutSection: (section: string, prompt: string) => void;

  // File upload
  attachments: FileAttachment[];
  addFiles: (files: File[]) => Promise<void>;
  removeFile: (id: string) => void;
  isUploading: boolean;
  uploadProgress: number;

  // Notifications
  snackbarMessage: string | null;
  clearSnackbar: () => void;
}

/**
 * Hook that manages all discovery chat logic including:
 * - Message handling and chat history
 * - File uploads and attachments
 * - Draft management and saving
 * - Quality score tracking
 */
export function useDiscoveryChat(
  options: UseDiscoveryChatOptions
): UseDiscoveryChatResult {
  const {
    initialChatHistory,
    initialSpecData,
    initialQualityScore,
    initialQualityReport,
  } = options;

  const { projectId, learningMode } = useWorkflow();

  // Convert loaded chat history to Message format, or use welcome message
  const initialMessages = useMemo<Message[]>(() => {
    if (initialChatHistory && initialChatHistory.length > 0) {
      return initialChatHistory.map((msg, index) => ({
        id: `loaded-${index}`,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp).getTime(),
      }));
    }
    return [WELCOME_MESSAGE];
  }, [initialChatHistory]);

  // Message state
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  // Draft and quality state
  const [draft, setDraft] = useState<ProblemStatementDraft | null>(() => {
    if (initialSpecData && 'problem' in initialSpecData) {
      return initialSpecData as ProblemStatementDraft;
    }
    return null;
  });
  const [qualityScore, setQualityScore] = useState<number>(
    initialQualityScore ?? 0
  );
  const [qualityScoreLabel, setQualityScoreLabel] =
    useState<string>('Design Readiness');
  const [sectionFeedback, setSectionFeedback] = useState<SectionFeedback[]>(
    () => {
      if (initialQualityReport?.section_feedback) {
        return initialQualityReport.section_feedback;
      }
      return [];
    }
  );
  const [qualityReport, setQualityReport] = useState<QualityReport | null>(
    initialQualityReport ?? null
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // File upload hook
  const {
    attachments,
    addFiles: addFilesInternal,
    removeFile,
    uploadAll,
    clearAll,
    isUploading,
    uploadProgress,
  } = useFileUpload({
    projectId: projectId || '',
    onUploadComplete: (attachment) => {
      logger.debug('File uploaded:', attachment.name);
    },
    onUploadError: (attachment, uploadError) => {
      setSnackbarMessage(
        `Upload failed for ${attachment.name}: ${uploadError.message}`
      );
    },
    onValidationError: (errors) => {
      setSnackbarMessage(errors.join('\n'));
    },
  });

  // Reset all state when projectId changes (user switches projects)
  // This ensures we don't show stale data from the previous project
  useEffect(() => {
    // Reset messages: use initialChatHistory if available, otherwise show welcome message
    if (initialChatHistory && initialChatHistory.length > 0) {
      setMessages(
        initialChatHistory.map((msg, index) => ({
          id: `loaded-${index}`,
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.timestamp).getTime(),
        }))
      );
    } else {
      setMessages([WELCOME_MESSAGE]);
    }

    // Reset draft state
    if (initialSpecData && 'problem' in initialSpecData) {
      setDraft(initialSpecData as ProblemStatementDraft);
    } else {
      setDraft(null);
    }

    // Reset quality state
    setQualityScore(initialQualityScore ?? 0);
    setQualityScoreLabel('Design Readiness');

    if (initialQualityReport) {
      setQualityReport(initialQualityReport);
      setSectionFeedback(initialQualityReport.section_feedback ?? []);
    } else {
      setQualityReport(null);
      setSectionFeedback([]);
    }

    // Clear any errors from previous project
    setError(null);
    setSnackbarMessage(null);
  }, [
    projectId,
    initialChatHistory,
    initialSpecData,
    initialQualityScore,
    initialQualityReport,
  ]);

  // Handle chat messages (with optional attachments)
  const sendMessage = useCallback(
    async (content: string, fileAttachments?: FileAttachment[]) => {
      if (!projectId) {
        setError(
          'No project selected. Please create or select a project first.'
        );
        return;
      }

      // Prepare attachment info for the message
      const messageAttachments: MessageAttachment[] =
        fileAttachments?.map((a) => ({
          id: a.id,
          name: a.name,
          type: a.mimeType,
          category: a.category,
          preview: a.preview,
        })) || [];

      // Add user message optimistically
      const newMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content,
        timestamp: Date.now(),
        attachments:
          messageAttachments.length > 0 ? messageAttachments : undefined,
      };
      setMessages((prev) => [...prev, newMessage]);
      setIsLoading(true);
      setError(null);

      try {
        // Upload files first if there are any pending
        let fileIds: string[] | undefined;
        if (fileAttachments && fileAttachments.length > 0) {
          fileIds = await uploadAll();
          if (fileIds.length === 0 && fileAttachments.length > 0) {
            setError('File uploads failed. Please try again.');
            setIsLoading(false);
            return;
          }
        }

        // Send chat message with file IDs
        const response: ChatResponse = await workflowService.chat(
          projectId,
          content,
          learningMode,
          fileIds
        );

        // Add assistant response
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.message,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Clear attachments after successful send
        clearAll();

        // Update draft from structured_data if present
        if (response.structured_data && 'problem' in response.structured_data) {
          setDraft(response.structured_data as ProblemStatementDraft);
        }

        // Update quality score
        if (response.quality_score !== undefined) {
          setQualityScore(response.quality_score);
        }

        // Update quality report and section feedback
        if (response.quality_report) {
          setQualityReport(response.quality_report);
          if (response.quality_report.section_feedback) {
            setSectionFeedback(response.quality_report.section_feedback);
          }
          if (response.quality_report.score_label) {
            setQualityScoreLabel(response.quality_report.score_label);
          }
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to send message';
        setError(errorMessage);
        logger.error('Chat error:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [projectId, learningMode, uploadAll, clearAll]
  );

  // Handle save button click - calls API to persist changes
  const saveDraft = useCallback(
    async (newDraft: ProblemStatementDraft) => {
      if (!projectId) return;

      setIsSaving(true);
      try {
        const response = await workflowService.updateDiscoveryDraft(
          projectId,
          newDraft
        );
        setDraft(newDraft);
        setQualityScore(response.quality_score);
        if (response.quality_report?.section_feedback) {
          setSectionFeedback(response.quality_report.section_feedback);
        }
        setSnackbarMessage('Draft saved successfully');
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to save draft';
        setSnackbarMessage(`Save failed: ${errorMessage}`);
        throw err; // Re-throw so the editor knows save failed
      } finally {
        setIsSaving(false);
      }
    },
    [projectId]
  );

  // Handle "ask about this section" click
  const askAboutSection = useCallback(
    (_section: string, prompt: string) => {
      sendMessage(prompt);
    },
    [sendMessage]
  );

  // Wrap addFiles to match expected signature
  const addFiles = useCallback(
    async (files: File[]) => {
      await addFilesInternal(files);
    },
    [addFilesInternal]
  );

  // Calculate clarity score as percentage
  const clarityPercent = Math.round(qualityScore * 100);

  return {
    // Messages
    messages,

    // Loading/error states
    isLoading,
    error,
    clearError: useCallback(() => setError(null), []),

    // Draft and quality
    draft,
    qualityScore,
    qualityScoreLabel,
    sectionFeedback,
    qualityReport,
    clarityPercent,

    // Save state
    isSaving,

    // Actions
    sendMessage,
    saveDraft,
    askAboutSection,

    // File upload
    attachments,
    addFiles,
    removeFile,
    isUploading,
    uploadProgress,

    // Notifications
    snackbarMessage,
    clearSnackbar: useCallback(() => setSnackbarMessage(null), []),
  };
}
